import * as THREE from "three";
import { sparkleMaterial } from "./views/rareShiny";

/**
 * Ambient sparkles over the whole map while a rare lives (M36 §5, "the game visuals change"): a slow snow of motes
 * drifting up from the ground and twinkling in the rare's two colours, denser as the night deepens, gone with it.
 * One `THREE.Points` draw of `Count` sprites, additive, no texture (rareShiny.ts sparkleMaterial), positions updated
 * on the CPU (≈ 1 µs per sprite), never drawn while its weight is 0.
 */
export class RareWorld {
  static readonly Count = 140;
  /** World size of a sprite (cells) and how fast they rise (cells/s). */
  static readonly Size = 0.15;
  static readonly Rise = 0.18;
  /** How high above the ground a mote lives before it wraps back down (cells). */
  static readonly Ceiling = 2.6;

  readonly points: THREE.Points;
  private readonly geo = new THREE.BufferGeometry();
  private readonly mat = sparkleMaterial();
  private readonly pos = new Float32Array(RareWorld.Count * 3);
  private readonly col = new Float32Array(RareWorld.Count * 3);
  private readonly size = new Float32Array(RareWorld.Count);
  private readonly base = new Float32Array(RareWorld.Count * 4);   // x, z, phase, speed
  /** The ground's height under each mote, read once (the terrain's noise is not free per frame). */
  private readonly floor = new Float32Array(RareWorld.Count);
  private readonly a = new THREE.Color();
  private readonly b = new THREE.Color();
  private time = 0;

  /** `w`×`h` cells, the map's ground (the sprites cover it and a margin around it). `groundAt(x, z)` its height. */
  constructor(w: number, h: number, groundAt: (x: number, z: number) => number) {
    this.geo.setAttribute("position", new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute("color", new THREE.BufferAttribute(this.col, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute("size", new THREE.BufferAttribute(this.size, 1).setUsage(THREE.DynamicDrawUsage));
    this.geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(w / 2, 1, h / 2), Math.hypot(w, h));
    this.points = new THREE.Points(this.geo, this.mat);
    this.points.frustumCulled = false;
    this.points.renderOrder = 4;
    this.points.visible = false;
    for (let i = 0; i < RareWorld.Count; i++) {
      this.base[i * 4] = -1.5 + Math.random() * (w + 2);
      this.base[i * 4 + 1] = -1.5 + Math.random() * (h + 2);
      this.base[i * 4 + 2] = Math.random();
      this.base[i * 4 + 3] = 0.6 + Math.random() * 0.8;
      this.floor[i] = groundAt(this.base[i * 4], this.base[i * 4 + 1]);
    }
  }

  setColours(a: number, b: number): void { this.a.setHex(a); this.b.setHex(b); }

  /** Per frame with the night's sparkle weight (0..1, rareLook.ts RareGlow); nothing is drawn at 0. */
  update(dt: number, weight: number, reduced: boolean): void {
    const on = weight > 0.01 && !reduced;
    this.points.visible = on;
    if (!on) return;
    this.time += dt;
    const t = this.time, n = RareWorld.Count;
    // fewer motes as the weight falls: the last ones out are the far ones (index order is random over the map)
    const shown = Math.round(n * Math.min(1, weight));
    for (let i = 0; i < n; i++) {
      const x = this.base[i * 4], z = this.base[i * 4 + 1], ph = this.base[i * 4 + 2], sp = this.base[i * 4 + 3];
      const life = (ph + t * RareWorld.Rise * sp / RareWorld.Ceiling) % 1;      // 0 at the ground → 1 at the ceiling
      const y = this.floor[i] + 0.05 + life * RareWorld.Ceiling;
      this.pos[i * 3] = x + Math.sin(t * 0.7 + ph * 20) * 0.25; this.pos[i * 3 + 1] = y; this.pos[i * 3 + 2] = z + Math.cos(t * 0.6 + ph * 17) * 0.25;
      const tw = Math.max(0, Math.sin(t * (3 + 4 * sp) + ph * 40));
      const fade = Math.sin(Math.PI * life) * (i < shown ? 1 : 0);
      const k = (0.5 + 2.2 * tw * tw) * fade * Math.min(1, weight * 1.2);
      const m = i % 3 === 0 ? this.b : this.a;
      this.col[i * 3] = m.r * k; this.col[i * 3 + 1] = m.g * k; this.col[i * 3 + 2] = m.b * k;
      this.size[i] = RareWorld.Size * (0.6 + 0.6 * tw) * (fade > 0 ? 1 : 0);
    }
    (this.geo.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    (this.geo.attributes.color as THREE.BufferAttribute).needsUpdate = true;
    (this.geo.attributes.size as THREE.BufferAttribute).needsUpdate = true;
  }

  dispose(): void { this.points.removeFromParent(); this.geo.dispose(); this.mat.dispose(); }
}
